/**
 * durum.tsx
 *
 * NE İŞE YARAR : `15` §9'un **altı durumu** — her liste ve detay ekranının karşılamak
 *                zorunda olduğu haller
 * KAYNAK SPEC  : 15-arayuz-standardi.md §9 (durum ekranları) · §9.1 (hata mesajı kuralı)
 *                §13 (hata metin + ikon) · 04-api-sozlesmesi.md §2.6 (hata kodları)
 *                ADR-007 §9 (`404` / `403` ayrımı)
 * BAĞLI OLDUĞU : @mantine/core · @tabler/icons-react · src/api/hata.ts · src/metin
 * KULLANAN     : Liste ve detay ekranlarının tamamı
 * DİKKAT       : 1) ALTISI BİRDEN ZORUNLUDUR (`15` §9 — *"biri eksikse ekran
 *                   tamamlanmamıştır"*). Bu yüzden hepsi tek dosyada ve tek tipte
 *                   toplandı: ekran yazarken hangisinin unutulduğu ancak liste bir
 *                   yerde duruyorsa görülür.
 *                2) YÜKLENİYOR **İSKELET**TİR, DÖNEN ÇARK DEĞİL (`15` §9). Çark
 *                   sayfanın yapısını yok eder ve içerik gelince yerleşim sıçrar;
 *                   iskelet, gelecek olanın şeklini şimdiden gösterir.
 *                3) BOŞ DURUM *"KAYIT YOK"* DEMEZ (`15` §9). Neden boş olduğunu **ve**
 *                   yapılabilecek işlemi söyler; aksi hâlde kullanıcı arıza mı yoksa
 *                   filtre mi olduğunu bilemez.
 *                4) `404` VE `403` **AYNI GÖRÜNMEZ** — ama sebebi ters (`15` §9 · ADR-007
 *                   §9). Sunucu ikisini zaten ayırıyor: scope dışı kayıt `404`, scope
 *                   içi ama izinsiz kayıt `403`. Arayüz de ayrı metin gösterir çünkü
 *                   kullanıcının yapacağı şey farklıdır: biri *"böyle bir kayıt yok"*,
 *                   diğeri *"var ama yetkiniz yok"*. **Varlığı sızdıran taraf sunucu
 *                   değildir** — `404`ü zaten scope dışı için o üretiyor.
 *                5) TEKNİK HATA METNİ GÖSTERİLMEZ (`15` §9). Sunucunun kullanıcı mesajı
 *                   gösterilir (`04` §9 · §9.1); kısıt adı, tablo adı ve SQL parçası
 *                   hiçbir ekranda görünmez. `HataUyarisi` bunu zaten uyguluyor.
 *                6) BAĞLANTI YOK DURUMU AYRI (`15` §9): *"yeniden dene"* butonu bulunur
 *                   ve **yazılmamış veri korunur** — bu yüzden bileşen hiçbir şeyi
 *                   sıfırlamaz, yalnız çizer. Sıfırlama kararı çağıranındır.
 * SON GÜNCELLEME: 2026-08-31 04:15:00
 */
import { Button, Center, Group, Skeleton, Stack, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconInbox,
  IconLock,
  IconPlugConnectedX,
  IconSearchOff,
} from '@tabler/icons-react';
import type { ComponentType, ReactElement, ReactNode } from 'react';

import { ApiHatasi } from '../api/hata';
import { metin, type MetinAnahtari } from '../metin';

/** `15` §9'un altı durumu — isimleri spec'teki satırların karşılığıdır. */
export type EkranDurumu =
  | 'YUKLENIYOR'
  | 'BOS'
  | 'HATA'
  | 'YETKISIZ'
  | 'BULUNAMADI'
  | 'BAGLANTI_YOK';

/**
 * Bir `ApiHatasi`yı ekran durumuna çevirir (`04` §2.6 → `15` §9).
 *
 * Eşleme burada tek yerde durur; her ekran kendi `switch`ini yazsaydı biri `403`ü
 * unutur ve kullanıcı *"beklenmeyen hata"* görürdü.
 */
export function hatadanDurum(hata: unknown): EkranDurumu {
  if (!(hata instanceof ApiHatasi)) {
    return 'HATA';
  }
  switch (hata.kod) {
    case 'BAGLANTI_YOK':
      return 'BAGLANTI_YOK';
    case 'PERMISSION_DENIED':
      return 'YETKISIZ';
    case 'NOT_FOUND':
    case 'GONE':
      return 'BULUNAMADI';
    default:
      return 'HATA';
  }
}

/** İskelet — yükleniyor durumu (başlıktaki not 2). */
export function Iskelet({ satir = 4 }: { satir?: number }): ReactElement {
  return (
    <Stack gap="sm" py="xs">
      {Array.from({ length: satir }, (_, sira) => (
        <Skeleton key={sira} height={20} radius="sm" />
      ))}
    </Stack>
  );
}

/** Ortak boş/hata/yetkisiz gövdesi — ikon + başlık + açıklama + eylem. */
function DurumKutusu({
  Ikon,
  baslik,
  aciklama,
  eylem,
}: {
  Ikon: ComponentType<{ size?: number }>;
  baslik: string;
  aciklama: string;
  eylem?: ReactNode | undefined;
}): ReactElement {
  return (
    <Center py="xl">
      <Stack gap="sm" align="center" maw={440}>
        {/* Metin + ikon birlikte; yalnız renkle anlatılmaz (`15` §13). */}
        <Ikon size={32} />
        <Text size="sm" fw={600} ta="center">
          {baslik}
        </Text>
        <Text size="sm" c="notr.7" ta="center">
          {aciklama}
        </Text>
        {eylem === undefined ? null : <Group mt="xs">{eylem}</Group>}
      </Stack>
    </Center>
  );
}

/**
 * `15` §9'un altı durumundan birini çizer.
 *
 * `YUKLENIYOR` iskelete, geri kalanı kutuya düşer. `bosBaslik`/`bosAciklama`
 * **zorunludur**: boş durumun ne olduğunu ekran bilir, bu bileşen bilemez (not 3).
 */
export function DurumEkrani({
  durum,
  hata,
  bosBaslik,
  bosAciklama,
  bosEylem,
  yenidenDene,
  iskeletSatiri,
}: {
  durum: EkranDurumu;
  /** `HATA` durumunda sunucunun mesajı buradan okunur (`15` §9.1). */
  hata?: unknown;
  bosBaslik?: string | undefined;
  bosAciklama?: string | undefined;
  bosEylem?: ReactNode | undefined;
  yenidenDene?: (() => void) | undefined;
  iskeletSatiri?: number | undefined;
}): ReactElement {
  if (durum === 'YUKLENIYOR') {
    return <Iskelet {...(iskeletSatiri === undefined ? {} : { satir: iskeletSatiri })} />;
  }

  const yenidenDeneButonu =
    yenidenDene === undefined ? undefined : (
      <Button variant="default" onClick={yenidenDene}>
        {metin('ortak.yenidenDene')}
      </Button>
    );

  if (durum === 'BOS') {
    return (
      <DurumKutusu
        Ikon={IconInbox}
        baslik={bosBaslik ?? metin('durum.bosBaslik')}
        aciklama={bosAciklama ?? metin('durum.bosAciklama')}
        eylem={bosEylem}
      />
    );
  }

  if (durum === 'YETKISIZ') {
    // Başlıktaki not 4 — kaydın varlığını sunucu zaten ele vermiş durumda (`403`).
    return (
      <DurumKutusu
        Ikon={IconLock}
        baslik={metin('durum.yetkisizBaslik')}
        aciklama={metin('durum.yetkisizAciklama')}
      />
    );
  }

  if (durum === 'BULUNAMADI') {
    return (
      <DurumKutusu
        Ikon={IconSearchOff}
        baslik={metin('durum.bulunamadiBaslik')}
        aciklama={metin('durum.bulunamadiAciklama')}
      />
    );
  }

  if (durum === 'BAGLANTI_YOK') {
    // Başlıktaki not 6 — yazılmamış veri korunur; bileşen hiçbir şeyi sıfırlamaz.
    return (
      <DurumKutusu
        Ikon={IconPlugConnectedX}
        baslik={metin('durum.baglantiBaslik')}
        aciklama={metin('durum.baglantiAciklama')}
        eylem={yenidenDeneButonu}
      />
    );
  }

  // `HATA` — sunucunun kendi mesajı gösterilir (`15` §9.1, başlıktaki not 5).
  return (
    <DurumKutusu
      Ikon={IconAlertTriangle}
      baslik={metin('durum.hataBaslik')}
      aciklama={hata instanceof ApiHatasi ? hata.message : metin('hata.beklenmeyen')}
      eylem={yenidenDeneButonu}
    />
  );
}

/** Ekranların boş durum metnini sözlükten seçmesi için kısayol (`15` §14). */
export function bosMetin(baslik: MetinAnahtari, aciklama: MetinAnahtari): {
  bosBaslik: string;
  bosAciklama: string;
} {
  return { bosBaslik: metin(baslik), bosAciklama: metin(aciklama) };
}
